import { UserRoleEnum } from 'src/app/enums/enums';
import { environment } from 'src/environments/environment';

export interface MenuItem {
    label: string;
    icon: string;
    routerLink?: string | string[];
    visible?: boolean | ((userRole: UserRoleEnum) => boolean);
    items?: MenuItem[];
}

export interface MenuModule {
    label: string;
    icon: string;
    visible?: boolean | ((userRole: UserRoleEnum) => boolean);
    items: MenuItem[];
}

/** Visible si features.operationalControl === true; si el flag no existe, fallback a !production. */
export function isOperationalControlEnabled(): boolean {
    const flag = environment.features?.operationalControl;
    if (typeof flag === 'boolean') {
        return flag;
    }
    return !environment.production;
}

export class MenuConfig {
    static getMenuModules(userRole: UserRoleEnum): MenuModule[] {
        const configAllAdmin = [UserRoleEnum.AdminCompany, UserRoleEnum.AdminWorkCenter];
        const configAdminCia = [UserRoleEnum.AdminCompany];

        const isRoleIn = (roles: UserRoleEnum[]) => roles.includes(userRole);
        const isRole = (role: UserRoleEnum) => userRole === role;
        const isNotRole = (role: UserRoleEnum) => userRole !== role;

        return [
            // 1. Administración
            {
                label: 'Administración',
                icon: 'pi pi-fw pi-cog',
                visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root),
                items: [
                    {
                        label: 'Usuarios',
                        icon: 'pi pi-fw pi-users',
                        routerLink: '/admin/users',
                        visible: isRoleIn(configAdminCia) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Compañias',
                        icon: 'pi pi-fw pi-building',
                        routerLink: '/admin/company',
                        visible: isRole(UserRoleEnum.Root) || isRole(UserRoleEnum.AdminCompany)
                    },
                    {
                        label: 'Centro de trabajo',
                        icon: 'pi pi-fw pi-briefcase',
                        routerLink: '/admin/workcenter',
                        visible: isRoleIn(configAllAdmin)
                    },
                    {
                        label: 'Empleados',
                        icon: 'pi pi-fw pi-users',
                        routerLink: '/admin/user-contract',
                        visible: isRoleIn(configAllAdmin)
                    },
                    {
                        label: 'Cargos de trabajo',
                        icon: 'pi pi-fw pi-id-card',
                        routerLink: '/admin/positions',
                        visible: isRole(UserRoleEnum.AdminCompany)
                    },
                    {
                        label: 'Motivos de solicitud',
                        icon: 'pi pi-fw pi-question-circle',
                        routerLink: '/admin/request-reasons',
                        visible: isRole(UserRoleEnum.Root)
                    }
                ]
            },
            // 2. Gestión Documental (oculto en sidebar; rutas siguen en app-routing)
            {
                label: 'Gestión Documental',
                icon: 'pi pi-fw pi-file',
                visible: false,
                items: [
                    {
                        label: 'Planear',
                        icon: 'pi pi-fw pi-calendar',
                        routerLink: '/document-management/plan',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Hacer',
                        icon: 'pi pi-fw pi-check-circle',
                        routerLink: '/document-management/do',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Verificar',
                        icon: 'pi pi-fw pi-eye',
                        routerLink: '/document-management/check',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Actuar',
                        icon: 'pi pi-fw pi-send',
                        routerLink: '/document-management/act',
                        visible: isNotRole(UserRoleEnum.Root)
                    }
                ]
            },
            // 3. Control Operacional / TAR (flag features.operationalControl; fallback: visible si no es production)
            {
                label: 'Control Operacional',
                icon: 'pi pi-fw pi-sliders-h',
                visible: isOperationalControlEnabled() && (isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Employee) || isRole(UserRoleEnum.Root)),
                items: [
                    {
                        label: 'Asignación TAR',
                        icon: 'pi pi-fw pi-calendar-plus',
                        routerLink: '/operational-control/tar-assignment',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Employee) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Editor de plantillas TAR',
                        icon: 'pi pi-fw pi-file-edit',
                        routerLink: '/operational-control/form-engine/builder',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Colaboradores Externos',
                        icon: 'pi pi-fw pi-users',
                        routerLink: '/operational-control/external-collaborators',
                        // Oculto: la ruta/feature todavía no está implementada
                        visible: false
                    }
                ]
            },
            // 4. EPPs (casco SVG de Font Awesome helmet-safety)
            {
                label: 'EPPs',
                icon: 'icon-helmet-safety',
                visible: isNotRole(UserRoleEnum.Root),
                items: [
                    {
                        label: 'Matriz de EPPs',
                        icon: 'pi pi-fw pi-exclamation-triangle',
                        routerLink: '/epp/list',
                        visible: isRoleIn(configAdminCia)
                    },
                    {
                        label: 'Categorías Epp',
                        icon: 'pi pi-fw pi-tags',
                        routerLink: '/admin/epp-categories',
                        visible: isRole(UserRoleEnum.AdminCompany)
                    },
                    {
                        label: 'Solicitar EPP',
                        icon: 'pi pi-fw pi-pencil',
                        routerLink: '/epp/request/create/home',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'EPPs Solicitados',
                        icon: 'pi pi-fw pi-envelope',
                        routerLink: '/epp/request/list',
                        visible: isRoleIn(configAllAdmin)
                    },
                    {
                        label: 'Ver mis Solicitudes',
                        icon: 'pi pi-fw pi-envelope',
                        routerLink: '/epp/request/my-requests',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Inventario EPP',
                        icon: 'pi pi-fw pi-box',
                        routerLink: '/epp/inventory/list',
                        visible: isRoleIn(configAllAdmin)
                    },
             
                    {
                        label: 'Entregar EPP',
                        icon: 'pi pi-fw pi-truck',
                        routerLink: '/epp/delivery/view',
                        visible: isRoleIn(configAllAdmin)
                    }
                ]
            },
            // 5. Inspecciones
            {
                label: 'Inspecciones',
                icon: 'pi pi-fw pi-clipboard',
                visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Employee) || isRole(UserRoleEnum.Root),
                items: [
                    {
                        label: 'Mis inspecciones',
                        icon: 'pi pi-fw pi-check-square',
                        routerLink: '/inspection/my',
                        visible: isNotRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Plantillas de inspección',
                        icon: 'pi pi-fw pi-file-edit',
                        routerLink: '/inspection/templates',
                        visible: isRoleIn(configAllAdmin)
                    },
                    {
                        label: 'Asignar inspección de campo',
                        icon: 'pi pi-fw pi-calendar-plus',
                        routerLink: '/inspection/assign',
                        visible: isRoleIn(configAllAdmin)
                    }
                ]
            },
            // 6. Reportes
            {
                label: 'Reportes',
                icon: 'pi pi-fw pi-chart-bar',
                visible:isRole(UserRoleEnum.AdminCompany),
                items: [
                    {
                        label: 'Tableros',
                        icon: 'pi pi-fw pi-chart-bar',
                        routerLink: '/powerbi',
                        visible:isRole(UserRoleEnum.AdminCompany)
                    }
                ]
            },
            // 7. ADM Contratistas/Aliados (oculto en sidebar)
            {
                label: 'ADM Contratistas/Aliados',
                icon: 'pi pi-fw pi-users',
                visible: false,
                items: [
                    {
                        label: 'Registrar',
                        icon: 'pi pi-fw pi-plus',
                        routerLink: '/contractor-administration',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Gestión',
                        icon: 'pi pi-fw pi-cog',
                        routerLink: '/contractor-administration/management',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Tipos de Contratista',
                        icon: 'pi pi-fw pi-tags',
                        routerLink: '/contractor-administration/contractor-types',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Documentos SMART',
                        icon: 'pi pi-fw pi-file',
                        routerLink: '/contractor-administration/smart-documents',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Administrar Solicitudes',
                        icon: 'pi pi-fw pi-file-edit',
                        routerLink: '/contractor-administration/task-requests',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    },
                    {
                        label: 'Gestión de Aliados Comerciales',
                        icon: 'pi pi-fw pi-handshake',
                        routerLink: '/contractor-administration/management/commercial-alliances',
                        visible: isRoleIn(configAllAdmin) || isRole(UserRoleEnum.Root)
                    }
                ]
            },
            // 8. Gestión (oculto en sidebar)
            {
                label: 'Gestión',
                icon: 'pi pi-fw pi-briefcase',
                visible: false,
                items: [
                    {
                        label: 'Inicio',
                        icon: 'pi pi-fw pi-home',
                        routerLink: '/management',
                        visible: true // Visible para todos por ahora
                    },
                    {
                        label: 'Completar Perfil',
                        icon: 'pi pi-fw pi-user-edit',
                        routerLink: '/management/complete-contractor-profile',
                        visible: true // Visible para todos por ahora
                    },
                    {
                        label: 'Completar Perfil Aliado Comercial',
                        icon: 'pi pi-fw pi-user-edit',
                        routerLink: '/management/complete-commercial-alliance-profile',
                        visible: true // Visible para aliados comerciales
                    }
                ]
            }
        ];
    }

    static evaluateVisibility(visible: boolean | ((userRole: UserRoleEnum) => boolean) | undefined, userRole: UserRoleEnum): boolean {
        if (visible === undefined) {
            return true;
        }
        if (typeof visible === 'boolean') {
            return visible;
        }
        return visible(userRole);
    }

    static buildMenuModel(modules: MenuModule[], userRole: UserRoleEnum): any[] {
        const model: any[] = [
            {
                items: [
                    { label: 'Inicio', icon: 'pi pi-fw pi-home', routerLink: ['/'] }
                ]
            }
        ];

        modules.forEach(module => {
            const moduleVisible = this.evaluateVisibility(module.visible, userRole);
            
            if (moduleVisible) {
                // Filtrar items visibles del módulo
                const visibleItems = module.items
                    .map(item => this.processMenuItem(item, userRole))
                    .filter(item => item !== null);

                if (visibleItems.length > 0) {
                    model.push({
                        label: module.label,
                        icon: module.icon,
                        visible: true,
                        items: visibleItems
                    });
                }
            }
        });

        return model;
    }

    static processMenuItem(item: MenuItem, userRole: UserRoleEnum): any | null {
        const itemVisible = this.evaluateVisibility(item.visible, userRole);
        
        if (!itemVisible) {
            return null;
        }

        const processedItem: any = {
            label: item.label,
            icon: item.icon,
            visible: true
        };

        if (item.routerLink) {
            processedItem.routerLink = item.routerLink;
        }

        if (item.items && item.items.length > 0) {
            const visibleSubItems = item.items
                .map(subItem => this.processMenuItem(subItem, userRole))
                .filter(subItem => subItem !== null);
            
            if (visibleSubItems.length > 0) {
                processedItem.items = visibleSubItems;
            }
        }

        return processedItem;
    }
}

